import React from "react";
import { IconButton } from "@mui/material";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import { FaEdit } from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import { convertCamelToDisplayName } from "./utils.js";

export const renderTable = ({
  data,
  headers,
  config,
  isListComponent,
  callbacks,
  expandedCells,
  toggleExpandCell,
  showAllRows,
  setShowAllRows,
  maxRows = 3,
  style = {},
}) => {
  const maxDisplayRows = showAllRows ? data : data.slice(0, maxRows);
  const isSingleFieldOrSubtable = isListComponent && headers.length === 0;

  // Helper to render cell content
  const renderCellContent = (item, fieldKey, rowIndex, fieldConfig) => {
    let displayValue;
    if (isListComponent) {
      const configName =
        config?.fieldNameMap?.[fieldKey] || fieldKey.toLowerCase();
      displayValue = item[configName] || item[fieldKey];
      if (typeof displayValue === "undefined" || displayValue === null) {
        displayValue = "N/A";
      } else if (typeof displayValue === "object") {
        displayValue = JSON.stringify(displayValue);
      } else if (typeof displayValue === "boolean") {
        displayValue = displayValue.toString();
      }
    } else {
      displayValue = item[fieldKey] || "N/A";
      if (typeof displayValue === "object") {
        displayValue = JSON.stringify(displayValue);
      } else if (typeof displayValue === "boolean") {
        displayValue = displayValue.toString();
      }
      if (config?.list_subtable) {
        const cachedObject =
          config.dataCache?.[config.list_subtable + "Lookup"]?.[item[fieldKey]];
        if (cachedObject) {
          const field = config.list_config?.[0];
          if (field?.dropdown_display_template) {
            displayValue = field.dropdown_display_template.replace(
              /\[([^\]]+)\]/g,
              (match, key) => cachedObject[key] || match
            );
          }
        }
      }
    }

    const isExpanded = expandedCells[`${rowIndex}-${fieldKey}`] || false;
    const textColor = rowIndex % 2 === 0 ? "#292929" : "#f0f0f0";

    return (
      <td
        key={fieldKey}
        style={{
          padding: "8px",
          minWidth:
            config?.table?.find((col) => col.name === fieldKey)?.minWidth ||
            "150px",
          maxWidth: "200px",
          color: textColor,
          whiteSpace: "normal",
          wordBreak: "break-word",
          overflow: "hidden",
          textOverflow: "ellipsis",
          border: "none",
          position: "relative",
          ...style.cell,
        }}
        onClick={() => toggleExpandCell(rowIndex, fieldKey)}
      >
        <div
          style={{
            display: "-webkit-box",
            WebkitLineClamp: isExpanded ? "none" : 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            lineHeight: "1.5",
            maxHeight: isExpanded ? "none" : "4.5em",
            cursor: "pointer",
            color: textColor,
          }}
          onClick={
            isListComponent && callbacks.showEditInput
              ? (e) => callbacks.showEditInput(e, rowIndex)
              : undefined
          }
        >
          {displayValue}
        </div>
        {String(displayValue).length > 50 && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              toggleExpandCell(rowIndex, fieldKey);
            }}
            sx={{
              position: "absolute",
              bottom: "2px",
              right: "2px",
              zIndex: 10,
              backgroundColor: rowIndex % 2 === 0 ? "#f0f0f0" : "#848587",
              padding: "4px",
              cursor: "pointer",
              "&:hover": {
                backgroundColor: rowIndex % 2 === 0 ? "#e0e0e0" : "#6b6b6b",
              },
            }}
          >
            {isExpanded ? (
              <UnfoldLessIcon sx={{ color: textColor, fontSize: "16px" }} />
            ) : (
              <UnfoldMoreIcon sx={{ color: textColor, fontSize: "16px" }} />
            )}
          </IconButton>
        )}
      </td>
    );
  };

  // Common table structure
  const tableContent = (
    <div
      style={{
        overflowX: "auto",
        maxWidth: "100%",
        ...style.container,
      }}
    >
      <table
        style={{
          borderCollapse: "separate",
          borderRadius: "10px",
          width: "100%",
          maxWidth: "100%",
          tableLayout: "auto",
          marginTop: 10,
          border: "2px solid #1E2A44",
          borderSpacing: 0,
          overflow: "hidden",
          ...style.table,
        }}
      >
        <thead>
          <tr
            style={{
              borderBottom: "2px solid #FFFFFF",
              backgroundColor: "#4d35ff",
              color: "#FFFFFF",
              ...style.headerRow,
            }}
          >
            <th
              style={{
                padding: "8px",
                textAlign: "left",
                width: "10%",
                textTransform: "capitalize",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                border: "none",
                ...style.headerCell,
              }}
            >
              Index
            </th>
            {isSingleFieldOrSubtable ? (
              <th
                style={{
                  padding: "8px",
                  textAlign: "left",
                  minWidth: "150px",
                  textTransform: "capitalize",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  border: "none",
                  ...style.headerCell,
                }}
              >
                Item
              </th>
            ) : (
              headers.map((header) => {
                const columnConfig = config?.table?.find(
                  (col) => col.name === header
                );
                return (
                  <th
                    key={header}
                    style={{
                      padding: "8px",
                      textAlign: "left",
                      minWidth: columnConfig?.minWidth || "150px",
                      maxWidth: "200px",
                      textTransform: "capitalize",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      border: "none",
                      ...style.headerCell,
                    }}
                  >
                    {convertCamelToDisplayName(header)}
                  </th>
                );
              })
            )}
            {isListComponent && (
              <th
                style={{
                  padding: "8px",
                  textAlign: "left",
                  width: "20%",
                  textTransform: "capitalize",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  border: "none",
                  ...style.headerCell,
                }}
              >
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody style={{ backgroundColor: "#202345", ...style.body }}>
          {maxDisplayRows.map((item, index) => {
            const rowIndex = index;
            const textColor = rowIndex % 2 === 0 ? "#292929" : "#f0f0f0";
            return (
              <tr
                key={item[config?.list_display_field] || index}
                style={{
                  backgroundColor: rowIndex % 2 === 0 ? "#f0f0f0" : "#848587",
                  color: textColor,
                  borderBottom: "1px solid #1E2A44",
                  ...style.row,
                }}
              >
                <td
                  style={{
                    padding: "8px",
                    width: "10%",
                    color: textColor,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    maxWidth: "200px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    border: "none",
                    position: "relative",
                    ...style.cell,
                  }}
                  onClick={() => toggleExpandCell(rowIndex, "index")}
                >
                  <div
                    style={{
                      display: "-webkit-box",
                      WebkitLineClamp: expandedCells[`${rowIndex}-index`]
                        ? "none"
                        : 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      lineHeight: "1.5",
                      maxHeight: expandedCells[`${rowIndex}-index`]
                        ? "none"
                        : "4.5em",
                      textAlign: "center",
                      color: textColor,
                      cursor: "pointer",
                    }}
                  >
                    {rowIndex + 1}
                  </div>
                  {String(rowIndex + 1).length > 50 && (
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpandCell(rowIndex, "index");
                      }}
                      sx={{
                        position: "absolute",
                        bottom: "2px",
                        right: "2px",
                        zIndex: 10,
                        backgroundColor:
                          rowIndex % 2 === 0 ? "#f0f0f0" : "#848587",
                        padding: "4px",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor:
                            rowIndex % 2 === 0 ? "#e0e0e0" : "#6b6b6b",
                        },
                      }}
                    >
                      {expandedCells[`${rowIndex}-index`] ? (
                        <UnfoldLessIcon
                          sx={{ color: textColor, fontSize: "16px" }}
                        />
                      ) : (
                        <UnfoldMoreIcon
                          sx={{ color: textColor, fontSize: "16px" }}
                        />
                      )}
                    </IconButton>
                  )}
                </td>
                {isSingleFieldOrSubtable
                  ? renderCellContent(
                      item,
                      config?.list_display_field || "item",
                      rowIndex,
                      config
                    )
                  : headers.map((header) =>
                      renderCellContent(item, header, rowIndex, config)
                    )}
                {isListComponent && (
                  <td
                    style={{
                      padding: "8px",
                      width: "20%",
                      color: textColor,
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      maxWidth: "200px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      border: "none",
                      position: "relative",
                      ...style.cell,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        gap: "10px",
                        zIndex: "1000",
                      }}
                    >
                      <button
                        onClick={(e) => callbacks.showEditInput?.(e, rowIndex)}
                        className="plus-btn primary-gradient-btn"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "5px",
                          color: textColor,
                        }}
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={(e) => callbacks.handleRemove?.(e, rowIndex)}
                        className="plus-btn primary-gradient-btn"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "5px",
                          color: textColor,
                        }}
                      >
                        <IoMdClose />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      {data.length > maxRows && (
        <div
          style={{
            marginTop: "10px",
            display: "flex",
            justifyContent: "start",
            ...style.footer,
          }}
        >
          <button
            onClick={() => setShowAllRows(!showAllRows)}
            type="button"
            className="primary-gradient-btn submit-btn"
            style={{
              padding: "5px 15px",
              background: "linear-gradient(to right, #3b82f6, #1e40af)",
              color: "#fff",
              border: "none",
              borderRadius: "20px",
              cursor: "pointer",
              ...style.button,
            }}
          >
            {showAllRows ? "See Less" : "See More"}
          </button>
        </div>
      )}
    </div>
  );

  return tableContent;
};
